import React, { useState, useEffect } from "react";
import { Form, Radio, Checkbox, Input, Button, Card, Typography, message, Spin, Progress, Select } from "antd";
import { useParams } from "react-router-dom";
import { getData, createDataUnAuth } from "../../util/API";
import "./style.less";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const formItemLayout = {
  labelCol: { xs: { span: 24 }, sm: { span: 24 } },
  wrapperCol: { xs: { span: 24 }, sm: { span: 24 } },
};

const CSATSurvey = (props) => {
  const { form } = props;
  const { getFieldDecorator, validateFields, setFieldsValue } = form;

  const [loading, setLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [surveyName, setSurveyName] = useState("");
  const [surveyDescription, setSurveyDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  const [thankYou, setThankYou] = useState(false);
  const [showLogo, setShowLogo] = useState(false);

  const { ticketId } = useParams();

  useEffect(() => {
    if (ticketId) {
      fetchSurveyForm(ticketId);
    }
  }, [ticketId]);

  useEffect(() => {
    if (questions.length > 0) {
      setFieldsValue(responses);
    }
  }, [questions]);

  useEffect(() => {
    if (questions.length > 0 && currentQuestion >= 0) {
      const prevQuestionId = String(questions[currentQuestion]?.id);
      if (responses[prevQuestionId] !== undefined) {
        setFieldsValue({ [prevQuestionId]: responses[prevQuestionId] });
      }
    }
  }, [currentQuestion]);

  const fetchSurveyForm = async (ticketId) => {
    setLoading(true);
    try {
      const res = await getData(`/management/csat-template/?ticket=${ticketId}`);
      if (res.data) {
        const survey = res.data.data[0];
        setTemplateId(survey.id);
        setSurveyName(survey.name);
        setSurveyDescription(survey.description);
        setQuestions(survey.data);
        setShowLogo(survey.show_logo);
        
        const initialResponses = {};
        survey.data.forEach((q) => {
          initialResponses[q.id] = q.type === "checkboxes" ? [] : "";
        });

        setResponses(initialResponses);
      }
    } catch (error) {
      message.error("Failed to load survey");
    } finally {
      setLoading(false);
    }
  };

  const submitSurveyForm = async (payload) => {
    setLoading(true);
    try {
      const res = await createDataUnAuth(`/management/csat-response/`, payload);
      if (res.data.status === 200) {
      setThankYou(true);
      form.resetFields();
      setResponses({});
      setCurrentQuestion(0);
      }
    } catch (error) {
      message.error("Failed to submit survey form!");
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value, type) => {
    setResponses((prev) => ({ ...prev, [key]: value }));
    setFieldsValue({ [key]: value });

    if (type === "multiple-choice" || type === "dropdown") {
      if (currentQuestion < questions.length - 1) {
        setTimeout(() => {
          nextQuestion();
        }, 300);
      }
    }
  };

  const nextQuestion = () => {
    if (!questions[currentQuestion]) return;
    validateFields([String(questions[currentQuestion].id)], (err, values) => {
      if (!err) {
        handleValueChange(questions[currentQuestion].id, values[String(questions[currentQuestion].id)]);
        setCurrentQuestion((prev) => prev + 1);
      }
    });
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateFields((err, values) => {
      if (!err) {
        setLoading(true);
        const formattedResponses = Object.entries({ ...responses, ...values }).map(([questionId, answer]) => ({
          question_id: parseInt(questionId, 10),
          answer: Array.isArray(answer) ? answer.join(", ") : answer,
          source: "web"
        }));

        const finalPayload = {
          ticket: ticketId,
          data: formattedResponses,
          template: templateId,
        };

        submitSurveyForm(finalPayload);
      }
    });
  };

  const progress = questions.length > 0 ? Math.round(((currentQuestion + 1) / questions.length) * 100) : 0;

  return (
    <div className="survey-wrap">
      <Card>
        {showLogo && (
          <div className="survey-header">
            <img src="/techlocal.png" width="200" alt="Techlocal Logo" />
          </div>
        )}
        {loading ? (
          <div className="survey-loading">
            <Spin size="large" />
          </div>
        ) : (
          questions.length > 0 ? (
            <>
              {thankYou ? (
                <>
                  <div style={{textAlign: 'center', marginTop: 60, color: 'green', fontSize: 'larger'}}>
                    <p>Your feedback has been submitted successfully.</p>
                    <p>Thank You!</p>
                  </div>
                </>
              ) : (
                <>
                  <Title level={3} style={{ textAlign: "center", marginBottom: "20px" }}>{surveyName}</Title>
                  <Paragraph style={{ textAlign: "center", marginBottom: "30px" }}>{surveyDescription}</Paragraph>
                  <Form {...formItemLayout} layout="vertical" style={{ marginTop: "50px" }}>
                    <Form.Item
                      key={questions[currentQuestion]?.id}
                      label={
                        questions.length > 0 && questions[currentQuestion] ? (
                          <div style={{display: "flex", flexDirection: "column"}}>
                          <Title level={4}>
                            {`${currentQuestion + 1}. ${questions[currentQuestion].questionText}`}
                          </Title>
                          {questions[currentQuestion].note && (
                            <Paragraph style={{ textAlign: "left", marginBottom: "0px" }}>{`${questions[currentQuestion].note}`}</Paragraph>
                          )}
                          </div>
                        ) : null
                      }>
                      {questions.length > 0 && questions[currentQuestion] && getFieldDecorator(String(questions[currentQuestion].id), {
                        initialValue: responses[String(questions[currentQuestion].id)],
                        rules: questions[currentQuestion].required
                          ? [{ required: true, message: "Please complete this field" }]
                          : []
                      })(
                        questions[currentQuestion].type === "multiple-choice" ? (
                          <Radio.Group key={questions[currentQuestion]?.id} onChange={(e) => handleValueChange(questions[currentQuestion].id, e.target.value, "multiple-choice")}>
                            {questions[currentQuestion].options.map((option) => (
                              <Radio key={option} value={option}>{option}</Radio>
                            ))}
                          </Radio.Group>
                        ) : questions[currentQuestion].type === "checkboxes" ? (
                          <Checkbox.Group key={questions[currentQuestion]?.id} onChange={(values) => handleValueChange(questions[currentQuestion].id, values, "checkboxes")}>
                            {questions[currentQuestion].options.map((option) => (
                              <Checkbox key={option} value={option}>{option}</Checkbox>
                            ))}
                          </Checkbox.Group>
                        ) : questions[currentQuestion].type === "short-answer" ? (
                          <Input key={questions[currentQuestion]?.id} placeholder={questions[currentQuestion].placeholderText} onChange={(e) => handleValueChange(questions[currentQuestion].id, e.target.value)} />
                        ) : questions[currentQuestion].type === "paragraph" ? (
                          <Input.TextArea rows={4} placeholder={questions[currentQuestion].placeholderText} onChange={(e) => handleValueChange(questions[currentQuestion].id, e.target.value)} />
                        ) : questions[currentQuestion].type === "dropdown" ? (
                          <Select key={questions[currentQuestion]?.id} onChange={(value) => handleValueChange(questions[currentQuestion].id, value, "dropdown")}>
                            {questions[currentQuestion].options.map((option) => (
                              <Option key={option} value={option}>{option}</Option>
                            ))}
                          </Select>
                        ) : null
                      )}
                    </Form.Item>
                    <Progress percent={progress} status="active" />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                      {currentQuestion > 0 && <Button onClick={prevQuestion}>Previous</Button>}
                      {currentQuestion < questions.length - 1 ? (
                        <Button type="primary" onClick={nextQuestion}>Next</Button>
                      ) : (
                        <Button type="primary" onClick={handleSubmit} loading={loading}>Submit</Button>
                      )}
                    </div>
                  </Form>
                </>
              )
              }
            </>
          ) : <Paragraph style={{ textAlign: "center" }}>No questions available</Paragraph>
        )}
      </Card>
    </div>
  );
};

const WrappedCSATSurvey = Form.create()(CSATSurvey);
export default WrappedCSATSurvey;
